import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, FunnelChart, Funnel as RFunnel, LabelList,
} from 'recharts';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Target, ChartBar, Funnel as FunnelIcon, Clock, MapPin, Robot, Lightning, Buildings, Trophy } from '@phosphor-icons/react';
import { HarpErpPulso } from '@/components/HarpErpPulso';
import { RankingSemanalERP } from '@/components/RankingSemanalERP';

const moneyFmt = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const SectionCard = ({ title, icon: Icon, children, accent = 'from-blue-500/15 to-purple-500/15' }) => (
  <div className={`bg-gradient-to-br ${accent} border border-[var(--border)] rounded-[16px] p-4 md:p-5`}>
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon size={20} weight="fill" className="text-[var(--primary)]" />}
      <h2 className="text-base md:text-lg font-black text-[var(--text)] heading-font">{title}</h2>
    </div>
    {children}
  </div>
);

const Funnel = ({ data }) => {
  if (!data?.stages?.length) return <p className="text-xs text-[var(--text-muted)]">Sin datos del mes</p>;
  const max = Math.max(...data.stages.map((s) => s.value), 1);
  return (
    <div className="space-y-2">
      {data.stages.map((s, i) => {
        const pct = (s.value / max) * 100;
        const colors = ['#3B82F6', '#8B5CF6', '#F59E0B', '#10B981'];
        return (
          <div key={i} data-testid={`funnel-stage-${i}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-bold text-[var(--text)]">{s.label}</span>
              <span className="font-black text-[var(--text)]">{s.value.toLocaleString('es-GT')}</span>
            </div>
            <div className="h-7 bg-[var(--surface-muted)] rounded-md overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{ width: `${Math.max(pct, 2)}%`, background: colors[i % colors.length] }}
              />
            </div>
          </div>
        );
      })}
      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-2">
          <p className="text-[9px] uppercase font-bold text-[var(--text-muted)]">Conv→Venta</p>
          <p className="text-base font-black text-blue-400">{data.conversion_overall_pct}%</p>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-2">
          <p className="text-[9px] uppercase font-bold text-[var(--text-muted)]">Hot→Cotiz</p>
          <p className="text-base font-black text-purple-400">{data.hot_to_quote_pct}%</p>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-2">
          <p className="text-[9px] uppercase font-bold text-[var(--text-muted)]">Cotiz→Venta</p>
          <p className="text-base font-black text-emerald-400">{data.quote_to_close_pct}%</p>
        </div>
      </div>
    </div>
  );
};

const Heatmap = ({ data }) => {
  const max = useMemo(() => Math.max(...(data?.cells || []).map((c) => c.count), 1), [data]);
  const grid = useMemo(() => {
    const g = Array.from({ length: 7 }, () => Array(24).fill(0));
    (data?.cells || []).forEach((c) => { g[c.day][c.hour] = c.count; });
    return g;
  }, [data]);
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        <div className="flex">
          <div className="w-10 shrink-0" />
          {Array.from({ length: 24 }).map((_, h) => (
            <div key={h} className="flex-1 text-center text-[9px] text-[var(--text-faint)]">{h}</div>
          ))}
        </div>
        {grid.map((row, d) => (
          <div key={d} className="flex items-center gap-0.5 mt-0.5">
            <div className="w-10 text-[10px] font-bold text-[var(--text-muted)] shrink-0">{DAYS[d]}</div>
            {row.map((v, h) => {
              const op = max ? Math.min(1, v / max) : 0;
              return (
                <div
                  key={h}
                  className="flex-1 h-5 rounded-sm"
                  style={{ background: `rgba(59,130,246,${0.08 + op * 0.85})` }}
                  title={`${DAYS[d]} ${h}:00 → ${v} msg`}
                />
              );
            })}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[var(--text-faint)] mt-2">Volumen de mensajes (cliente) por hora-día · últimos {data?.days || 30}d</p>
    </div>
  );
};

const TopProducts = ({ data, onPeriodChange, period }) => {
  if (!data?.items?.length) return <p className="text-xs text-[var(--text-muted)]">Sin cotizaciones en el periodo</p>;
  return (
    <>
      <div className="flex gap-1 mb-3">
        {[['month', 'Mes'], ['quarter', 'Trimestre']].map(([k, l]) => (
          <button
            key={k}
            onClick={() => onPeriodChange(k)}
            className={`px-2 py-1 text-[11px] font-bold rounded-md ${period === k ? 'bg-[var(--primary)] text-white' : 'bg-[var(--surface)] text-[var(--text-muted)]'}`}
            data-testid={`top-products-period-${k}`}
          >{l}</button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[var(--text-muted)] text-[10px] uppercase font-bold border-b border-[var(--border)]">
              <th className="text-left py-2 pr-2">#</th>
              <th className="text-left py-2 pr-2">Producto</th>
              <th className="text-right py-2 pr-2">Cant.</th>
              <th className="text-right py-2 pr-2">Cot.</th>
              <th className="text-right py-2">Monto</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((p, i) => (
              <tr key={i} className="border-b border-[var(--border)]/50 last:border-0">
                <td className="py-2 pr-2 text-[var(--text-muted)]">{i + 1}</td>
                <td className="py-2 pr-2 font-bold text-[var(--text)] truncate max-w-[200px]">{p.descripcion}</td>
                <td className="py-2 pr-2 text-right">{p.qty.toFixed(0)}</td>
                <td className="py-2 pr-2 text-right">{p.quote_count}</td>
                <td className="py-2 text-right font-bold text-emerald-400">{moneyFmt(p.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const VendorComparison = ({ data }) => {
  if (!data?.vendors?.length) return <p className="text-xs text-[var(--text-muted)]">Sin datos del mes</p>;
  return (
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer>
        <BarChart data={data.vendors} margin={{ top: 10, right: 10, left: 10, bottom: 50 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="vendor_name" angle={-30} textAnchor="end" interval={0} stroke="#94A3B8" style={{ fontSize: 10 }} />
          <YAxis yAxisId="left" stroke="#94A3B8" style={{ fontSize: 10 }} />
          <YAxis yAxisId="right" orientation="right" stroke="#94A3B8" style={{ fontSize: 10 }} />
          <Tooltip
            contentStyle={{ background: '#1E293B', border: '1px solid #334155', borderRadius: 8, fontSize: 11 }}
            formatter={(v, name) => name.includes('amount') ? [moneyFmt(v), name] : [v, name]}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="left" dataKey="sales_count" name="Ventas" fill="#10B981" />
          <Bar yAxisId="left" dataKey="quotes_count" name="Cotiz." fill="#3B82F6" />
          <Bar yAxisId="right" dataKey="conversion_pct" name="Conv %" fill="#F59E0B" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const ClosingProjection = ({ data }) => {
  if (!data) return null;
  const pct = data.projected_pct_of_goal;
  const statusColor = data.status === 'on_track' ? '#10B981' : data.status === 'at_risk' ? '#F59E0B' : '#DC2626';
  const statusLabel = data.status === 'on_track' ? 'En ritmo' : data.status === 'at_risk' ? 'En riesgo' : 'Fuera de meta';
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-3">
          <p className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Vendido al día {data.elapsed_days}</p>
          <p className="text-xl font-black text-emerald-400">{moneyFmt(data.sold_so_far)}</p>
          <p className="text-[10px] text-[var(--text-faint)] mt-0.5">{data.sale_count} ventas</p>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-3">
          <p className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Ritmo diario</p>
          <p className="text-xl font-black text-blue-400">{moneyFmt(data.daily_run_rate)}</p>
          <p className="text-[10px] text-[var(--text-faint)] mt-0.5">/ día</p>
        </div>
      </div>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-3">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase font-bold text-[var(--text-muted)]">Proyección a fin de mes</p>
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full font-black uppercase"
            style={{ background: `${statusColor}22`, color: statusColor }}
          >
            {statusLabel}
          </span>
        </div>
        <p className="text-2xl font-black" style={{ color: statusColor }}>
          {moneyFmt(data.projected_month_end)}
        </p>
        {data.goal_total > 0 ? (
          <>
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
              Meta agregada: {moneyFmt(data.goal_total)} · {pct ?? '—'}%
            </p>
            <div className="h-2 bg-[var(--surface-muted)] rounded-full overflow-hidden mt-2">
              <div className="h-full transition-all" style={{ width: `${Math.min(100, pct || 0)}%`, background: statusColor }} />
            </div>
          </>
        ) : (
          <p className="text-[10px] text-[var(--text-faint)] mt-0.5">Sin metas mensuales configuradas</p>
        )}
      </div>
    </div>
  );
};

const GeoHeatmap = ({ data }) => {
  if (!data?.departments?.length) return <p className="text-xs text-[var(--text-muted)]">Sin ventas con dirección este mes</p>;
  return (
    <div className="space-y-1.5">
      {data.departments.slice(0, 10).map((d, i) => {
        const max = data.departments[0].amount || 1;
        const pct = (d.amount / max) * 100;
        return (
          <div key={i}>
            <div className="flex justify-between text-xs mb-0.5">
              <span className="font-bold text-[var(--text)]">{d.department}</span>
              <span className="text-[var(--text-muted)]">{d.count} · {moneyFmt(d.amount)}</span>
            </div>
            <div className="h-3 bg-[var(--surface-muted)] rounded-md overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ResponseTime = ({ data }) => {
  if (!data?.by_vendor?.length && !data?.by_platform?.length) {
    return <p className="text-xs text-[var(--text-muted)]">Sin conversaciones analizables en {data?.days || 30}d</p>;
  }
  const fmt = (m) => m > 60 ? `${(m / 60).toFixed(1)} h` : `${Math.round(m)} m`;
  return (
    <div className="space-y-3">
      {!!data.by_platform.length && (
        <div>
          <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1.5">Por canal</p>
          <div className="grid grid-cols-2 gap-2">
            {data.by_platform.map((p) => (
              <div key={p.platform} className="bg-[var(--surface)] border border-[var(--border)] rounded-md p-2.5">
                <p className="text-[10px] font-bold uppercase text-[var(--text-muted)]">{p.platform}</p>
                <p className="text-lg font-black text-[var(--text)]">{fmt(p.avg)}</p>
                <p className="text-[9px] text-[var(--text-faint)]">{p.count} conv · mediana {fmt(p.median)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {!!data.by_vendor.length && (
        <div>
          <p className="text-[10px] uppercase font-bold text-[var(--text-muted)] mb-1.5">Por vendedor (top 8)</p>
          <table className="w-full text-xs">
            <tbody>
              {data.by_vendor.slice(0, 8).map((v) => (
                <tr key={v.vendor_id} className="border-b border-[var(--border)]/40 last:border-0">
                  <td className="py-1.5 font-bold text-[var(--text)]">{v.vendor_name}</td>
                  <td className="py-1.5 text-right text-emerald-400 font-black">{fmt(v.avg)}</td>
                  <td className="py-1.5 text-right text-[10px] text-[var(--text-faint)]">{v.count} conv</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const BusinessHealth = ({ data }) => {
  if (!data?.businesses?.length) return <p className="text-xs text-[var(--text-muted)]">Sin negocios configurados</p>;
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-[var(--text-muted)] text-[10px] uppercase font-bold border-b border-[var(--border)]">
          <th className="text-left py-1.5">Negocio</th>
          <th className="text-right py-1.5">Conv.</th>
          <th className="text-right py-1.5">Ventas</th>
          <th className="text-right py-1.5">Conv %</th>
        </tr>
      </thead>
      <tbody>
        {data.businesses.map((b) => (
          <tr key={b.business_id} className="border-b border-[var(--border)]/40 last:border-0">
            <td className="py-1.5 font-bold text-[var(--text)] truncate max-w-[160px]">{b.name}</td>
            <td className="py-1.5 text-right">{b.conversations}</td>
            <td className="py-1.5 text-right text-emerald-400 font-black">{b.sales}</td>
            <td className="py-1.5 text-right font-bold text-blue-400">{b.conversion_pct}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const DashboardGerencial = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [kpiSending, setKpiSending] = useState(false);
  const [kpiDownloading, setKpiDownloading] = useState(false);
  const now = new Date();
  const [kpiMonth, setKpiMonth] = useState(now.getMonth() + 1);
  const [kpiYear, setKpiYear] = useState(now.getFullYear());
  const [kpiEmpresa, setKpiEmpresa] = useState('todas');
  const [kpiExporting, setKpiExporting] = useState(false);

  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);

  const loadKpiOnScreen = async () => {
    try {
      setKpiLoading(true);
      const res = await api.get(`/reportes/kpi-mensual?year=${kpiYear}&month=${kpiMonth}`);
      setKpiData(res.data);
    } catch (e) {
      toast.error('Error cargando metas del mes');
    } finally { setKpiLoading(false); }
  };

  const downloadKpiExcel = async () => {
    try {
      setKpiExporting(true);
      const res = await api.get(`/reportes/kpi-mensual/excel?year=${kpiYear}&month=${kpiMonth}&empresa=${kpiEmpresa}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reporte_${kpiYear}_${String(kpiMonth).padStart(2, '0')}_${kpiEmpresa}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (e) {
      toast.error('Error exportando a Excel');
    } finally { setKpiExporting(false); }
  };

  const downloadKpiPdf = async () => {
    try {
      setKpiDownloading(true);
      const res = await api.get(`/reportes/kpi-mensual/pdf?year=${kpiYear}&month=${kpiMonth}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `KPI_FinDeMes_${kpiYear}_${String(kpiMonth).padStart(2, '0')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF del reporte fin de mes descargado');
    } catch (e) {
      toast.error('Error generando el PDF del reporte');
    } finally {
      setKpiDownloading(false);
    }
  };

  const sendKpiEmail = async () => {
    try {
      setKpiSending(true);
      const res = await api.post(`/reportes/kpi-mensual/enviar?year=${kpiYear}&month=${kpiMonth}`);
      const sent = res.data?.sent || [];
      toast.success(sent.length ? `Reporte enviado a: ${sent.join(', ')}` : 'Reporte procesado (revisa destinatarios verificados en Resend)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando el reporte');
    } finally {
      setKpiSending(false);
    }
  };

  const load = async (p = period) => {
    try {
      setLoading(true);
      const res = await api.get(`/dashboard-gerencial/overview?period=${p}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period); /* eslint-disable-next-line */ }, [period]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="page-dashboard-gerencial">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center shrink-0">
            <ChartBar size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-[var(--text)] heading-font">Dashboard Gerencial</h1>
            <p className="text-sm text-[var(--text-muted)] mt-0.5">
              Vista 360° para la dirección · {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString('es-GT') : '—'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={kpiEmpresa}
            onChange={(e) => setKpiEmpresa(e.target.value)}
            data-testid="select-kpi-empresa"
            className="px-2 py-2 text-xs font-black rounded-lg bg-[var(--surface)] border border-blue-500/40 text-blue-500"
          >
            <option value="todas">🏢 Resumen (ambas)</option>
            <option value="cyh">Complementos (CYH)</option>
            <option value="sa">Soluciones (SA)</option>
          </select>
          <select
            value={kpiMonth}
            onChange={(e) => setKpiMonth(Number(e.target.value))}
            data-testid="select-kpi-mes"
            className="px-2 py-2 text-xs font-bold rounded-lg bg-[var(--surface)] border border-[var(--border)] text-[var(--text)]"
          >
            {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={kpiYear}
            onChange={(e) => setKpiYear(Number(e.target.value))}
            data-testid="select-kpi-anio"
            className="px-2 py-2 text-xs font-bold rounded-lg bg-[var(--surface)] border border-[var(--border)] text-[var(--text)]"
          >
            {Array.from({ length: 3 }, (_, i) => now.getFullYear() - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={loadKpiOnScreen}
            disabled={kpiLoading}
            data-testid="btn-kpi-mensual-pantalla"
            className="px-3 py-2 text-xs font-bold rounded-lg bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] hover:border-[var(--primary)] transition-colors disabled:opacity-50"
          >
            {kpiLoading ? 'Cargando…' : '🎯 Ver Metas en Pantalla'}
          </button>
          <button
            onClick={downloadKpiPdf}
            disabled={kpiDownloading}
            data-testid="btn-kpi-mensual-pdf"
            className="px-3 py-2 text-xs font-bold rounded-lg bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] hover:border-[var(--primary)] transition-colors disabled:opacity-50"
          >
            {kpiDownloading ? 'Generando…' : '📄 PDF Fin de Mes'}
          </button>
          <button
            onClick={downloadKpiExcel}
            disabled={kpiExporting}
            data-testid="btn-kpi-mensual-excel"
            className="px-3 py-2 text-xs font-bold rounded-lg bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] hover:border-[var(--primary)] transition-colors disabled:opacity-50"
          >
            {kpiExporting ? 'Exportando…' : '⬇ Excel'}
          </button>
          <button
            onClick={sendKpiEmail}
            disabled={kpiSending}
            data-testid="btn-kpi-mensual-email"
            className="px-3 py-2 text-xs font-bold rounded-lg bg-[var(--primary)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {kpiSending ? 'Enviando…' : '📧 Enviar Reporte Fin de Mes'}
          </button>
        </div>
      </div>

      {kpiData && (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 mb-6" data-testid="metas-mes-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-black heading-font uppercase tracking-wide">🎯 Metas del Mes — {kpiData.month_label}</h3>
            <button onClick={() => setKpiData(null)} className="text-xs text-[var(--text-muted)] hover:text-[var(--text)]" data-testid="metas-mes-cerrar">✕ Cerrar</button>
          </div>
          <p className="text-[11px] text-[var(--text-muted)] mb-3">Metas mensuales (se reinician cada mes) vs lo realizado. Cada celda: hecho / meta y % de cumplimiento. Verde ≥100% · Ámbar ≥70% · Rojo &lt;70%.</p>

          {/* Comparativo por empresa y general — siempre visible */}
          {kpiData.crm_resumen && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4" data-testid="comparativo-empresas">
              {[
                { k: 'cyh', titulo: 'Complementos (CYH)', erp: kpiData.empresas?.['Complementos y Herrajes']?.total },
                { k: 'sa', titulo: 'Soluciones (SA)', erp: kpiData.empresas?.['Soluciones Arquitectónicas']?.total },
                { k: 'total', titulo: '🏢 General (ambas)', erp: kpiData.erp_total },
              ].map(({ k, titulo, erp }) => {
                const r = kpiData.crm_resumen[k] || {};
                const activo = (kpiEmpresa === k) || (kpiEmpresa === 'todas' && k === 'total');
                return (
                  <div key={k} className={`border-2 rounded-[12px] p-3 ${activo ? 'border-blue-500/60 bg-blue-500/5' : 'border-[var(--border)]'}`} data-testid={`resumen-empresa-${k}`}>
                    <p className="text-[10px] font-black uppercase tracking-wide text-[var(--text-muted)]">{titulo}</p>
                    <p className="text-sm font-black text-[var(--text)] mt-1">
                      Q{Number(r.ventas || 0).toLocaleString()} <span className="text-[var(--text-faint)] font-bold">/ Q{Number(r.meta || 0).toLocaleString()} meta</span>
                    </p>
                    <div className="h-1.5 bg-[var(--surface-muted)] rounded-full overflow-hidden mt-1.5">
                      <div className={`h-full ${r.pct >= 100 ? 'bg-emerald-500' : r.pct >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(100, r.pct || 0)}%` }} />
                    </div>
                    <p className="text-[10px] font-bold text-[var(--text-muted)] mt-1">
                      {r.pct != null ? `${Math.round(r.pct)}% de meta CRM` : 'sin meta'} · ERP: Q{Number(erp || 0).toLocaleString()}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] border-b border-[var(--border)]">
                  <th className="text-left py-2 pr-2">Vendedor</th>
                  <th className="text-center py-2">Llamadas</th>
                  <th className="text-center py-2">Visitas</th>
                  <th className="text-center py-2">Cotizaciones</th>
                  <th className="text-center py-2">Ventas CRM{kpiEmpresa === 'cyh' ? ' (CYH)' : kpiEmpresa === 'sa' ? ' (SA)' : ''}</th>
                  <th className="text-right py-2">Ventas ERP{kpiEmpresa === 'cyh' ? ' (CYH)' : kpiEmpresa === 'sa' ? ' (SA)' : ''}</th>
                  <th className="text-center py-2">Global</th>
                </tr>
              </thead>
              <tbody>
                {(kpiData.vendors || []).map((v) => {
                  const ventas = kpiEmpresa === 'cyh'
                    ? { done: v.sales_cyh, goal: v.sales_cyh_goal, pct: v.sales_cyh_pct, erp: v.erp_cyh }
                    : kpiEmpresa === 'sa'
                      ? { done: v.sales_sa, goal: v.sales_sa_goal, pct: v.sales_sa_pct, erp: v.erp_sa }
                      : { done: v.sales_amount, goal: v.sales_goal, pct: v.sales_pct, erp: v.erp_total };
                  const PctCell = ({ done, goal, pct, money }) => (
                    <td className="text-center py-2 text-xs">
                      <span className="font-bold">{money ? `Q${Number(done || 0).toLocaleString()}` : done}</span>
                      <span className="text-[var(--text-faint)]"> / {goal ? (money ? `Q${Number(goal).toLocaleString()}` : goal) : '—'}</span>
                      <p className={`font-black ${pct == null ? 'text-[var(--text-faint)]' : pct >= 100 ? 'text-emerald-500' : pct >= 70 ? 'text-amber-500' : 'text-red-500'}`}>
                        {pct == null ? 'sin meta' : `${Math.round(pct)}%`}
                      </p>
                    </td>
                  );
                  return (
                    <tr key={v.user_id} className="border-b border-[var(--border)] hover:bg-white/[0.02]" data-testid={`meta-row-${v.user_id}`}>
                      <td className="py-2 pr-2">
                        <p className="font-bold text-[var(--text)]">{v.name}</p>
                        <p className="text-[10px] text-[var(--text-faint)]">{v.branch || 'sin sucursal'}</p>
                      </td>
                      <PctCell done={v.calls} goal={v.calls_goal} pct={v.calls_pct} />
                      <PctCell done={v.visits} goal={v.visits_goal} pct={v.visits_pct} />
                      <PctCell done={v.quotes} goal={v.quotes_goal} pct={v.quotes_pct} />
                      <PctCell done={ventas.done} goal={ventas.goal} pct={ventas.pct} money />
                      <td className="text-right py-2 text-xs font-bold text-[var(--text)]">Q{Number(ventas.erp || 0).toLocaleString()}</td>
                      <td className="text-center py-2">
                        <span className={`text-sm font-black ${v.overall_pct == null ? 'text-[var(--text-faint)]' : v.overall_pct >= 100 ? 'text-emerald-500' : v.overall_pct >= 70 ? 'text-amber-500' : 'text-red-500'}`}>
                          {v.overall_pct == null ? '—' : `${Math.round(v.overall_pct)}%`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && !data ? (
        <p className="text-sm text-[var(--text-muted)]">Cargando…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="lg:col-span-2">
            <SectionCard title="Ventas HarpERP · facturación real (CyH + SA)" icon={Buildings} accent="from-emerald-500/15 to-blue-500/15">
              <HarpErpPulso embedded />
            </SectionCard>
          </div>

          <SectionCard title="Ranking de la semana · facturación ERP" icon={Trophy} accent="from-amber-500/15 to-orange-500/15">
            <RankingSemanalERP embedded />
          </SectionCard>

          <SectionCard title="Embudo de ventas IA" icon={FunnelIcon} accent="from-blue-500/15 to-purple-500/15">
            <Funnel data={data?.funnel} />
          </SectionCard>

          <SectionCard title="Proyección de cierre IA" icon={Target} accent="from-emerald-500/15 to-blue-500/15">
            <ClosingProjection data={data?.closing_projection} />
          </SectionCard>

          <SectionCard title="Heatmap mensajes (hora × día)" icon={Clock} accent="from-purple-500/10 to-pink-500/10">
            <Heatmap data={data?.heatmap} />
          </SectionCard>

          <SectionCard title="Comparativo entre vendedores" icon={ChartBar} accent="from-amber-500/10 to-emerald-500/10">
            <VendorComparison data={data?.vendor_comparison} />
          </SectionCard>

          <SectionCard title="Top productos cotizados" icon={Lightning} accent="from-rose-500/10 to-orange-500/10">
            <TopProducts data={data?.top_products} onPeriodChange={setPeriod} period={period} />
          </SectionCard>

          <SectionCard title="Ventas por departamento" icon={MapPin} accent="from-cyan-500/10 to-blue-500/10">
            <GeoHeatmap data={data?.geo_heatmap} />
          </SectionCard>

          <SectionCard title="Tiempo de respuesta" icon={Clock} accent="from-fuchsia-500/10 to-violet-500/10">
            <ResponseTime data={data?.response_time} />
          </SectionCard>

          <SectionCard title="Salud por negocio" icon={Buildings} accent="from-teal-500/10 to-emerald-500/10">
            <BusinessHealth data={data?.business_health} />
          </SectionCard>
        </div>
      )}
    </div>
  );
};

export default DashboardGerencial;
